import { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import { useTenant } from './TenantContext';
import { useDashboardData } from './DashboardDataContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingContext = createContext(null);

export function useOnboarding() {
  return useContext(OnboardingContext);
}

const STEPS = ['wallet_funded', 'customers_added', 'template_created', 'campaign_sent'];

export function OnboardingProvider({ children }) {
  const { getToken } = useTenant();
  const dashboardData = useDashboardData();

  const [checklistDismissed, setChecklistDismissed] = useState(false);
  const [dismissedTooltips, setDismissedTooltips] = useState([]);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const fetchedRef = useRef(false);

  // Fetch persisted onboarding state
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}/api/settings/onboarding`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setChecklistDismissed(data.checklist_dismissed);
          setDismissedTooltips(data.dismissed_tooltips || []);
          setCompletedSteps(data.completed_steps || []);
        }
      } catch (err) {
        console.error('Failed to load onboarding state:', err);
      } finally {
        setLoaded(true);
      }
    })();
  }, [getToken]);

  // Auto-detect completed steps from dashboard data
  const autoDetectedSteps = [];
  if (dashboardData) {
    const summary = dashboardData.dashboardSummary;
    const walletStatus = dashboardData.walletStatus;
    const walletBalance = walletStatus?.wallet_balance_cents || summary?.wallet_balance_cents || 0;

    if (walletBalance > 0) autoDetectedSteps.push('wallet_funded');
    if ((summary?.total_customers || 0) > 0) autoDetectedSteps.push('customers_added');
    if ((dashboardData.templates?.total || dashboardData.templates?.templates?.length || 0) > 0) autoDetectedSteps.push('template_created');
    if ((summary?.completed_campaigns || summary?.total_campaigns || 0) > 0) autoDetectedSteps.push('campaign_sent');
  }

  // Merge persisted + auto-detected steps
  const mergedSteps = [...new Set([...completedSteps, ...autoDetectedSteps])];
  const allStepsComplete = STEPS.every(s => mergedSteps.includes(s));

  // Persist helper
  const persistUpdate = useCallback(async (body) => {
    try {
      const token = await getToken();
      await fetch(`${BACKEND_URL}/api/settings/onboarding`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch (err) {
      console.error('Failed to persist onboarding update:', err);
    }
  }, [getToken]);

  const dismissChecklist = useCallback(() => {
    setChecklistDismissed(true);
    persistUpdate({ checklist_dismissed: true });
  }, [persistUpdate]);

  const dismissTooltip = useCallback((id) => {
    setDismissedTooltips(prev => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      persistUpdate({ dismiss_tooltip: id });
      return next;
    });
  }, [persistUpdate]);

  const isTooltipVisible = useCallback((id) => {
    return loaded && !dismissedTooltips.includes(id);
  }, [loaded, dismissedTooltips]);

  const openGuide = useCallback(() => setGuideOpen(true), []);
  const closeGuide = useCallback(() => setGuideOpen(false), []);

  return (
    <OnboardingContext.Provider value={{
      loaded,
      completedSteps: mergedSteps,
      checklistDismissed,
      allStepsComplete,
      dismissChecklist,
      dismissTooltip,
      isTooltipVisible,
      steps: STEPS,
      guideOpen,
      openGuide,
      closeGuide,
    }}>
      {children}
    </OnboardingContext.Provider>
  );
}
